import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchGroupGrowth } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

/**
 * A4.16 — Guruh a'zo o'sish grafigi. GET /admin/groups/:id/growth (backend
 * `ca9d67f`). `dialog_members.joined_at` bo'yicha kunlik qo'shilishlar (14 kun).
 */
export function GroupGrowth() {
  const t = useT()
  const { can } = useAuth()
  const { id = '' } = useParams()
  const [series, setSeries] = useState<{ day: string; joined: number }[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchGroupGrowth(Number(id), ac.signal)
      .then((s) => {
        if (alive) setSeries(s)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  const bars = useMemo<BarDatum[]>(
    () => series.map((d) => ({ label: d.day.slice(5), value: d.joined, title: d.day })),
    [series],
  )

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  return (
    <>
      <PageHeader
        title={t.groups.growth}
        screenId="A4.16"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : (
          <div className="max-w-3xl rounded-lg border border-border-default bg-surface-2 p-3">
            <div className="mb-2 text-sm font-medium text-secondary">{t.groups.growth}</div>
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <BarChart data={bars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
            )}
          </div>
        )}
      </div>
    </>
  )
}
